import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Paperclip, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { api, download, formatError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Card, Spinner } from "@/components/Field";
import { StatusBadge } from "@/components/StatusBadge";
import { MinuteDialog, MINUTE_KINDS } from "@/components/dialogs/MinuteDialog";

const Files = ({ m, canEdit, reload }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const act = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); } };
  const upload = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (files.length) act(async () => { for (const f of files) { const fd = new FormData(); fd.append("file", f); await api.post(`/minutes/${m.id}/files`, fd); } }, "Allegato caricato");
  };
  return (
    <Card testId="minute-files" className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Paperclip className="h-4 w-4" /> Allegati</h2>
        {canEdit && <Button data-testid="minute-file-upload" variant="outline" className="h-10" disabled={busy} onClick={() => ref.current?.click()}><Upload className="h-4 w-4 mr-1" /> Aggiungi</Button>}
        <input ref={ref} type="file" multiple hidden accept=".pdf,.doc,.docx,image/*" data-testid="minute-file-input" onChange={upload} />
      </div>
      {!m.files?.length ? <p className="text-sm text-slate-500">Nessun allegato.</p> : (
        <ul className="divide-y">
          {m.files.map((f) => (
            <li key={f.id} data-testid={`minute-file-${f.id}`} className="flex items-center gap-3 py-2">
              <span className="flex-1 truncate text-sm font-medium">{f.name}</span>
              <Button data-testid={`minute-file-download-${f.id}`} variant="ghost" size="icon" className="h-10 w-10" onClick={() => download(`/minutes/${m.id}/files/${f.id}`, f.name).catch((e) => toast.error(formatError(e)))} aria-label="Scarica"><Download className="h-4 w-4" /></Button>
              {canEdit && <Button data-testid={`minute-file-delete-${f.id}`} variant="ghost" size="icon" className="h-10 w-10 text-red-700" disabled={busy} onClick={() => window.confirm(`Eliminare ${f.name}?`) && act(() => api.delete(`/minutes/${m.id}/files/${f.id}`), "Allegato eliminato")} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default function MinuteDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const { data: m, error, reload } = useFetch(`/minutes/${id}`);
  const [edit, setEdit] = useState(false);
  if (error) return <p className="text-slate-500" data-testid="minute-not-found">Verbale non trovato.</p>;
  if (!m) return <Spinner />;
  const remove = async () => {
    if (!window.confirm("Eliminare questo verbale?")) return;
    try { await api.delete(`/minutes/${id}`); toast.success("Verbale eliminato"); nav("/verbali"); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-5" data-testid="minute-detail">
      <Link to="/verbali" className="inline-flex items-center gap-1 text-sm font-semibold text-primary"><ArrowLeft className="h-4 w-4" /> Tutti i verbali</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{m.title}</h1>
          <div className="text-slate-500 mt-1 flex flex-wrap items-center gap-2">{fmtDate(m.date)}{m.location && ` · ${m.location}`} <StatusBadge label={MINUTE_KINDS[m.kind]} /></div>
        </div>
        <div className="flex gap-2">
          {can("minutes", "edit") && <Button data-testid="minute-edit" variant="outline" className="h-11" onClick={() => setEdit(true)}><Pencil className="h-4 w-4 mr-1" /> Modifica</Button>}
          {can("minutes", "delete") && <Button data-testid="minute-delete" variant="outline" className="h-11 text-red-700" onClick={remove}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>
      {m.attendees && <Card className="p-5"><h2 className="font-semibold mb-1">Presenti</h2><p className="text-sm whitespace-pre-wrap text-slate-700">{m.attendees}</p></Card>}
      <Card testId="minute-body" className="p-5"><p className="whitespace-pre-wrap leading-relaxed text-slate-800">{m.body || "—"}</p></Card>
      <Files m={m} canEdit={can("minutes", "edit")} reload={reload} />
      <p className="text-xs text-slate-500">Creato da {m.created_by_name} il {fmtDateTime(m.created_at)}{m.updated_by_name && ` · modificato da ${m.updated_by_name}`}</p>
      <MinuteDialog open={edit} onOpenChange={setEdit} minute={m} onSaved={reload} />
    </div>
  );
}
